// src/components/layout/AdminLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  AppBar, Box, Drawer, IconButton, List, ListItemIcon, ListItemText,
  Toolbar, Typography, Divider, ListItemButton, Tooltip, Avatar, Menu,
  MenuItem, useTheme, useMediaQuery, Chip, // Added Chip
  Select, FormControl, InputLabel, CircularProgress, Button
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, PeopleAlt as PeopleIcon,
  VpnKey as ApiKeyIcon, Chat as ChatIcon, Code as WidgetIcon,
  Description as PromptIcon, // Tune as ModelIcon, // ModelIcon not used in nav
  Brightness4 as DarkModeIcon, Brightness7 as LightModeIcon, Logout as LogoutIcon,
  Build as ToolIcon, Add as AddIcon,
  // Dns as DnsIcon, // DnsIcon not used in nav
  Widgets as WidgetsCollectionIcon,
  Explore as ExploreIcon,
  // Storage as StorageIcon, // StorageIcon not used in nav
  Settings as SettingsAppIcon,
  HelpOutline as HelpOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as AgentWorkspaceIcon,
  Clear as ClearIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon // For credit display
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useAgentContext } from '../../context/AgentContext';
import { useBilling } from '../../context/BillingContext'; // <-- IMPORT useBilling
import { agentService } from '../../api/agentService';
import { toast } from 'react-toastify';

const drawerWidth = 260;

const GlobalNavSectionHeading = ({ open, text }) => (
  open ?
  <Typography
    variant="caption"
    sx={{
      pl: 2, pt: 2, pb: 0.5, display: 'block',
      color: 'text.secondary', fontWeight: 500,
      textTransform: 'uppercase', letterSpacing: '0.05em'
    }}
  >
    {text}
  </Typography> : <Divider sx={{my:1}}/>
);

const AdminLayout = ({ toggleTheme, currentTheme }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const [agentSelectorAnchorEl, setAgentSelectorAnchorEl] = useState(null);

  const { logout, user } = useAuth();
  const { currentAgentId, setCurrentAgentId, currentAgent, deselectAgent } = useAgentContext();
  const { accountStatus, isLoadingStatus: isLoadingBillingContext } = useBilling(); // <-- USE BILLING CONTEXT

  const [allAgentsList, setAllAgentsList] = useState([]);
  const [loadingAgentsList, setLoadingAgentsList] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchAgentsForSelector = async () => {
      setLoadingAgentsList(true);
      const res = await agentService.listAgents();
      if (!res.isError && Array.isArray(res)) {
        setAllAgentsList(res);
      } else {
        setAllAgentsList([]);
        // Consider if this toast is too noisy on every load if API is temporarily down
        // toast.error("Could not load agents for selector: " + (res.message || "Unknown error"));
      }
      setLoadingAgentsList(false);
    };
    if(user) { // Only fetch if user is logged in
        fetchAgentsForSelector();
    } else {
        setAllAgentsList([]);
        setLoadingAgentsList(false);
    }
  }, [user]); // Re-fetch if user changes (login/logout)

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleProfileMenuOpen = (event) => setProfileMenuAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchorEl(null);

  const handleAgentSelectorOpen = (event) => setAgentSelectorAnchorEl(event.currentTarget);
  const handleAgentSelectorClose = () => setAgentSelectorAnchorEl(null);

  const handleAgentSelect = (agentId) => {
    setCurrentAgentId(agentId);
    handleAgentSelectorClose();
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    // AuthContext's onAuthStateChange should handle redirect to /login
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && drawerOpen) setDrawerOpen(false);
  };

  const isPathActive = (path) => location.pathname.startsWith(path);
  const isAgentWorkspaceActive = location.pathname.startsWith('/agent-workspace');

  const globalNavItems = [
    { icon: <DashboardIcon />, text: 'Dashboard', path: '/dashboard' },
    { icon: <PeopleIcon />, text: 'All Agents', path: '/agents' },
    { isHeading: true, text: 'Global Resources' },
    { icon: <ExploreIcon />, text: 'Prompt Library', path: '/prompts' },
    { icon: <ToolIcon />, text: 'Tool Library', path: '/tools' },
    { icon: <WidgetsCollectionIcon />, text: 'Widget Templates', path: '/widgets' },
    { icon: <ChatIcon />, text: 'All Conversations', path: '/conversations' },
    { isHeading: true, text: 'Platform' },
    { icon: <ApiKeyIcon />, text: 'API Keys', path: '/apikeys' },
    { icon: <SettingsAppIcon />, text: 'Platform Settings', path: '/platform-settings' },
    { icon: <HelpOutlineIcon />, text: 'Help & Resources', path: '/help' },
  ];

  const CurrentAgentDisplay = () => (
    <Button
        variant="outlined"
        onClick={handleAgentSelectorOpen}
        size="small"
        sx={{
            ml: 2,
            textTransform: 'none',
            borderColor: currentAgent ? 'primary.main' : alpha(theme.palette.divider, 0.5), // Softer border when no agent
            color: currentAgent ? 'primary.main' : 'text.secondary',
            maxWidth: 250,
            minWidth: 150,
            '& .MuiButton-startIcon': { mr: 0.75 },
        }}
        startIcon={currentAgent ? <AgentWorkspaceIcon /> : <PeopleIcon />}
        endIcon={<ExpandMoreIcon />}
    >
        <Typography variant="body2" noWrap sx={{fontWeight: currentAgent ? 600 : 400}}>
            {loadingAgentsList && !currentAgent ? "Loading..." : (currentAgent ? currentAgent.name : "Select Agent")}
        </Typography>
    </Button>
  );

  const ThemeIconComponent = currentTheme === 'light' ? DarkModeIcon : LightModeIcon;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="default" // Uses theme's paper color essentially
        elevation={0} // Flat design based on theme component overrides
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          // borderBottom: 1, borderColor: 'divider', // This is now handled by MuiAppBar override in theme
          // backgroundColor: theme.palette.background.paper // Also handled by theme
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={handleDrawerToggle} edge="start" sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6" noWrap component={RouterLink} to="/dashboard"
            sx={{ fontWeight: 700, color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
          >
            DopomogAI
          </Typography>

          {user && <CurrentAgentDisplay />} {/* Only show if user is logged in */}

          <Box sx={{ flexGrow: 1 }} />

          {/* --- GLOBAL CREDIT DISPLAY --- */}
          {user && ( // Only show if user is logged in
            isLoadingBillingContext && !accountStatus ? ( // Show loader only if status is truly loading and not yet available
                <CircularProgress size={20} color="inherit" sx={{ mr: 1.5 }} />
            ) : accountStatus && accountStatus.balance !== null && accountStatus.balance !== undefined ? (
                <Tooltip title="View Billing & Account Details">
                <Chip
                    icon={<WalletIcon sx={{ fontSize: '1.1rem !important', color: theme.palette.primary.main }}/>}
                    label={`${accountStatus.balance.toFixed(accountStatus.balance % 1 === 0 ? 0 : 2)} Credits`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate('/billing')}
                    sx={{
                    mr: 1.5,
                    color: theme.palette.primary.main,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    },
                    height: '32px', // Match avatar height
                    '.MuiChip-label': { fontWeight: 500 }
                    }}
                />
                </Tooltip>
            ) : null /* Or a placeholder if balance is truly null after loading and user exists */
          )}
          {/* --- END GLOBAL CREDIT DISPLAY --- */}


          <Button variant="contained" color="primary" size="small" startIcon={<WidgetIcon />} onClick={() => navigate('/widget-test')} sx={{ borderRadius: '20px', fontWeight: 600, mr:1, py: '6px', px: '12px' }}>
            Widget Test
          </Button>
          <Tooltip title={`Switch to ${currentTheme === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 0.5 }}>
              <ThemeIconComponent />
            </IconButton>
          </Tooltip>
          {user && ( // Only show profile menu if user is logged in
            <Tooltip title="Account settings">
                <IconButton onClick={handleProfileMenuOpen} size="small" sx={{ ml: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', color: 'primary.contrastText' }}>
                    {user.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={profileMenuAnchorEl}
        open={Boolean(profileMenuAnchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3, // Slightly more pronounced for menus
          sx: { mt: 1.5, borderRadius: '8px', minWidth: 240 }, // Adjusted styling
        }}
      >
        <MenuItem disabled sx={{opacity: '0.7 !important', '&.Mui-disabled': { opacity: 0.7 } }}> {/* Ensure opacity for disabled */}
            <ListItemText
                primary={<Typography variant="body2" fontWeight="600" noWrap>{user?.email}</Typography>}
                secondary={<Typography variant="caption" color="text.secondary" noWrap>User ID: ...{user?.id?.slice(-6)}</Typography>} // Example: show part of user ID
            />
        </MenuItem>
        <Divider sx={{ my: 0.5}} />
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/billing'); }}>
            <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Billing & Subscription</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/platform-settings'); }}>
            <ListItemIcon><SettingsAppIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Account Settings</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5}} />
        <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" sx={{color: 'error.main'}}/></ListItemIcon>
            <ListItemText sx={{color: 'error.main'}}>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={agentSelectorAnchorEl}
        open={Boolean(agentSelectorAnchorEl)}
        onClose={handleAgentSelectorClose}
        PaperProps={{ sx: { maxHeight: 400, width: '280px', borderRadius: '8px', elevation: 3 } }}
      >
        {currentAgentId && (
          <>
            <MenuItem onClick={() => { deselectAgent(); handleAgentSelectorClose(); }}>
              <ListItemIcon><ClearIcon fontSize="small" color="error"/></ListItemIcon>
              <ListItemText primary="Exit Agent Workspace" primaryTypographyProps={{color: 'error.main', fontWeight: 500}} />
            </MenuItem>
            <Divider sx={{ my: 0.5}} />
          </>
        )}
        {loadingAgentsList ? (
          <MenuItem disabled sx={{justifyContent: 'center'}}><CircularProgress size={20} sx={{mr:1}}/>Loading agents...</MenuItem>
        ) : allAgentsList.length === 0 ? (
          <MenuItem disabled sx={{justifyContent: 'center'}}>No agents found.</MenuItem>
        ) : (
          allAgentsList.map(agentItem => (
            <MenuItem
              key={agentItem.id}
              selected={agentItem.id === currentAgentId}
              onClick={() => handleAgentSelect(agentItem.id)}
              sx={{ '&.Mui-selected': { fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.08)} }}
            >
              <ListItemText primary={agentItem.name} secondary={`ID: ...${agentItem.id.slice(-6)}`} />
            </MenuItem>
          ))
        )}
        <Divider sx={{ my: 0.5}} />
        <MenuItem onClick={() => { navigate('/agents/new'); handleAgentSelectorClose(); }}>
            <ListItemIcon><AddIcon fontSize="small"/></ListItemIcon>
            Create New Agent
        </MenuItem>
         <MenuItem onClick={() => { navigate('/agents'); handleAgentSelectorClose(); }}>
            <ListItemIcon><PeopleIcon fontSize="small"/></ListItemIcon>
            Manage All Agents
        </MenuItem>
      </Menu>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        ModalProps={{ keepMounted: true }} // Better for mobile performance
        sx={{
          width: drawerOpen ? drawerWidth : theme.spacing(9), flexShrink: 0,
          transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
          [`& .MuiDrawer-paper`]: {
            width: drawerOpen ? drawerWidth : theme.spacing(9),
            boxSizing: 'border-box',
            borderRight: { sm: `1px solid ${theme.palette.divider}` }, // Only show border on larger screens
            overflowX: 'hidden',
            transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
            // backgroundColor: theme.palette.background.default, // Use default background for a more integrated look
            pt: 1
          },
        }}
      >
        <Toolbar /> {/* Necessary for content to be below app bar */}
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
          <List component="nav" sx={{ px: drawerOpen ? 1.5 : 1 }}>
            {globalNavItems.map((item, index) => (
              item.isHeading ?
                <GlobalNavSectionHeading key={`heading-${index}`} open={drawerOpen} text={item.text} /> :
              (
              <ListItemButton
                key={item.path || `item-${index}`}
                selected={item.path ? isPathActive(item.path) && !isAgentWorkspaceActive : false}
                onClick={() => item.path && handleNavigation(item.path)}
                sx={{
                  borderRadius: '8px', // Consistent border radius
                  mb: 0.5, py: drawerOpen ? 1 : 1.5, px: drawerOpen ? 1.5 : 'auto',
                  minHeight: 48, justifyContent: drawerOpen ? 'initial' : 'center',
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18)},
                    '& .MuiListItemIcon-root': {color: 'primary.main'}
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.5)
                  }
                }}
              >
                <Tooltip title={!drawerOpen && !isMobile ? item.text : ""} placement="right">
                  <ListItemIcon sx={{
                    minWidth: 0, mr: drawerOpen ? 2 : 'auto', justifyContent: 'center',
                    color: (item.path && isPathActive(item.path) && !isAgentWorkspaceActive) ? 'primary.main' : 'text.secondary'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                </Tooltip>
                {drawerOpen && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                        fontWeight: (item.path && isPathActive(item.path) && !isAgentWorkspaceActive) ? 600 : 500,
                        fontSize: '0.9rem',
                        color: (item.path && isPathActive(item.path) && !isAgentWorkspaceActive) ? 'primary.main' : 'text.primary'
                    }}
                  />
                )}
              </ListItemButton>
            ))) }
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: theme.palette.background.default,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        <Toolbar /> {/* Spacer for the AppBar */}
        <Box sx={{
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            // Added padding here for the main content area
            p: { xs: 1.5, sm: 2, md: 3 } 
          }}
        >
          <Outlet /> {/* Page content rendered here */}
          <Box component="footer" sx={{
            py: 2, 
            // px removed as parent Box now provides horizontal padding
            mt: 'auto', 
            textAlign: 'center', 
            borderTop: 1, 
            borderColor:'divider', 
            backgroundColor: theme.palette.background.paper 
          }}>
            <Typography variant="caption" color="text.secondary">© {new Date().getFullYear()} DopomogAI. All Rights Reserved.</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;